import type { Rect, SelectionCombineMode } from '../core/types';
import { RenderTarget } from '../gl/renderTarget';
import type { Renderer } from '../gl/renderer';
import type { Texture } from '../gl/texture';
import type { ShapeRaster } from './shapes';

export const COMBINE_MODE_IDS: Record<SelectionCombineMode, number> = {
  replace: 0,
  add: 1,
  subtract: 2,
  intersect: 3,
  exclude: 5,
};

const COMBINE_INVERT = 4;
const MAX_BLUR_TAPS = 64;

/**
 * The active selection, stored as a document-sized 8-bit coverage mask. Every
 * destructive operation multiplies by this texture, which is why partial (feathered)
 * selections work everywhere without per-tool code.
 */
export class SelectionMask {
  private readonly mask: RenderTarget;
  /** Staging target for the shape being combined in. */
  private readonly shape: RenderTarget;
  private readonly scratch: RenderTarget;
  private emptyFlag = true;
  private boundsCache: Rect | null = null;
  private boundsValid = false;
  /** Kept so that Select - Reselect can bring the last selection back. */
  private previous: Uint8Array | null = null;

  constructor(gl: WebGL2RenderingContext, width: number, height: number) {
    this.mask = new RenderTarget(gl, width, height, 'r8', 'linear');
    this.shape = new RenderTarget(gl, width, height, 'r8', 'linear');
    this.scratch = new RenderTarget(gl, width, height, 'r8', 'linear');
    this.mask.clear(0, 0, 0, 0);
  }

  get texture(): Texture {
    return this.mask.texture;
  }

  get width(): number {
    return this.mask.width;
  }

  get height(): number {
    return this.mask.height;
  }

  get isEmpty(): boolean {
    return this.emptyFlag;
  }

  get bytes(): number {
    return this.mask.bytes + this.shape.bytes + this.scratch.bytes;
  }

  /** Texture to hand to shaders, or `null` when nothing is selected. */
  get activeTexture(): Texture | null {
    return this.emptyFlag ? null : this.mask.texture;
  }

  deselect(): void {
    if (!this.emptyFlag) this.previous = this.snapshot();
    this.mask.clear(0, 0, 0, 0);
    this.emptyFlag = true;
    this.boundsCache = null;
    this.boundsValid = true;
  }

  /** Select - Reselect. Returns false when nothing has been deselected yet. */
  reselect(): boolean {
    if (!this.previous) return false;
    this.restore(this.previous);
    return !this.emptyFlag;
  }

  selectAll(): void {
    // Upload explicit 255s — R8 clear() is not reliable on every driver, and a
    // silent zero-fill would mark the selection active with no marching ants.
    const filled = new Uint8Array(this.width * this.height);
    filled.fill(255);
    this.mask.texture.uploadPixels(
      { x: 0, y: 0, width: this.width, height: this.height },
      filled,
    );
    this.emptyFlag = false;
    this.boundsCache = { x: 0, y: 0, width: this.width, height: this.height };
    this.boundsValid = true;
  }

  /** Rasterised shape in, combined with whatever is already selected. */
  combineShape(
    renderer: Renderer,
    raster: ShapeRaster,
    mode: SelectionCombineMode,
    featherRadius: number,
  ): void {
    this.shape.clear(0, 0, 0, 0);
    this.shape.texture.uploadPixels(raster.rect, raster.data);
    if (featherRadius > 0.5) this.featherShape(renderer, featherRadius);

    renderer.combineMask(
      this.scratch.framebuffer,
      this.mask.texture,
      this.shape.texture,
      COMBINE_MODE_IDS[mode],
    );
    renderer.copy(this.mask.framebuffer, this.scratch.texture);
    this.invalidate();
  }

  invert(renderer: Renderer): void {
    renderer.combineMask(
      this.scratch.framebuffer,
      this.mask.texture,
      this.shape.texture,
      COMBINE_INVERT,
    );
    renderer.copy(this.mask.framebuffer, this.scratch.texture);
    this.invalidate();
  }

  snapshot(): Uint8Array {
    return this.mask.texture.readPixels();
  }

  restore(data: Uint8Array | null): void {
    if (!data) {
      this.deselect();
      return;
    }
    this.mask.texture.uploadPixels(
      { x: 0, y: 0, width: this.width, height: this.height },
      data,
    );
    this.invalidate();
    this.refresh();
  }

  /** Recomputes emptiness and bounds from a CPU readback. */
  refresh(): void {
    if (this.boundsValid) return;
    const data = this.mask.texture.readPixels();
    const { width, height } = this;
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < height; y += 1) {
      const row = y * width;
      for (let x = 0; x < width; x += 1) {
        if (data[row + x] === 0) continue;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
    if (maxX < 0) {
      this.emptyFlag = true;
      this.boundsCache = null;
    } else {
      this.emptyFlag = false;
      this.boundsCache = { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
    }
    this.boundsValid = true;
  }

  /** Bounding box of the selected area, or `null` when nothing is selected. */
  bounds(): Rect | null {
    this.refresh();
    return this.boundsCache;
  }

  resize(width: number, height: number): void {
    this.mask.resize(width, height);
    this.shape.resize(width, height);
    this.scratch.resize(width, height);
    this.deselect();
  }

  dispose(): void {
    this.mask.dispose();
    this.shape.dispose();
    this.scratch.dispose();
  }

  private featherShape(renderer: Renderer, radius: number): void {
    const taps = Math.min(MAX_BLUR_TAPS, Math.max(1, Math.round(radius)));
    const stride = radius / taps;
    const sigma = Math.max(0.6, taps / 2);
    renderer.blurPass(
      this.scratch.framebuffer,
      this.shape.texture,
      stride / this.width,
      0,
      taps,
      sigma,
    );
    renderer.blurPass(
      this.shape.framebuffer,
      this.scratch.texture,
      0,
      stride / this.height,
      taps,
      sigma,
    );
  }

  private invalidate(): void {
    this.boundsValid = false;
    this.emptyFlag = false;
  }
}
